import { useEffect, useState } from 'react';
import Navbar from 'components/Navbar';
import Footer from 'components/Footer';
import { Title, Subtitle, Text, TextButton } from 'theme/GlobalStyles';
import Button from 'components/Button';
import { useAppSelector, useAppDispatch } from 'redux/hooks';
import {
  selectIsLoading,
  selectError,
  selectEvents,
  selectTotalDocuments,
  selectNextQuery,
  selectPrevQuery,
  selectSignupPopupOpen,
  selectSuccessAlertOpen,
} from 'redux/events/selectors';
import {
  getEvents,
  getNextPageEvents,
  getPrevPageEvents,
} from 'redux/events/thunk';
import Loader from 'components/Loader';
import { renderDateRange } from 'utils/helperFunctions';
import Pagination from 'components/Pagination';
import { compareAsc } from 'date-fns';
import Alert from 'components/Alert';
import Popup from 'components/Popup';
import { Event } from 'api/models/Event';

import {
  PageWrapper,
  Content,
  Header,
  LoaderWrapper,
  Events,
  EventCard,
  ImageWrapper,
  EventImage,
  DarkOverlay,
  EventTitle,
  DetailsRow,
  Label,
  DetailsText,
  SignupsDisabledWrapper,
  SignupsDisabledText,
  ButtonsWrapper,
  LoadMoreEventsButtonWrapper,
} from './styled';
import SignupForm from './SignupForm';
import { hideSuccessAlert, setSignupPopupOpen } from 'redux/events/slice';
import Description from 'containers/EventsPage/Description';
import FilteringControls from './FilteringControls';

const rowsPerPage = 6;

export default function EventsPage() {
  const dispatch = useAppDispatch();

  const isLoading = useAppSelector(selectIsLoading);
  const error = useAppSelector(selectError);
  const events = useAppSelector(selectEvents);
  const totalEvents = useAppSelector(selectTotalDocuments);
  const nextQuery = useAppSelector(selectNextQuery);
  const prevQuery = useAppSelector(selectPrevQuery);
  const popupOpen = useAppSelector(selectSignupPopupOpen);
  const [descPopupOpen, setDescPopupOpen] = useState(false);
  const successAlertOpen = useAppSelector(selectSuccessAlertOpen);

  const [page, setPage] = useState(0);
  const [selectedEventId, setSelectedEventId] = useState('');
  const [selectedMonth, setSelectedMonth] = useState('all');
  const [selectedYear, setSelectedYear] = useState('all');
  // state for keeping current scroll position
  const [scrollPosition, setScrollPosition] = useState(0);

  const handleMonthSelect = (value: string) => {
    if (selectedYear === 'all' && value !== 'all') {
      setSelectedYear(new Date().getFullYear().toString());
    }

    setPage(0);
    setScrollPosition(0);
    setSelectedMonth(value);
  };

  const handleYearSelect = (value: string) => {
    setPage(0);
    setScrollPosition(0);
    setSelectedYear(value);
  };

  useEffect(() => {
    dispatch(
      getEvents({
        pageSize: rowsPerPage,
        selectedMonth,
        selectedYear: selectedYear === 'all' ? undefined : selectedYear,
      }),
    );
  }, [dispatch, selectedMonth, selectedYear]);

  useEffect(() => {
    if (page === 0 || !nextQuery) return;

    dispatch(
      getNextPageEvents({
        nextQuery,
        page,
        pageSize: rowsPerPage,
        totalDocuments: totalEvents,
        selectedMonth,
        selectedYear,
      }),
    );

    // save current scroll position
    setScrollPosition(window.scrollY);
  }, [page]);

  // Restore smoothly scroll position after events are loaded
  useEffect(() => {
    if (isLoading) return;

    const newScrollPosition = scrollPosition + (scrollPosition === 0 ? 0 : 600);

    window.scrollTo({
      top: newScrollPosition,
      behavior: 'smooth',
    });
  }, [isLoading, scrollPosition]);

  // hide success alert after 5 seconds
  useEffect(() => {
    if (!successAlertOpen) return;

    const timeout = setTimeout(() => {
      dispatch(hideSuccessAlert());
    }, 5000);

    return () => clearTimeout(timeout);
  }, [successAlertOpen]);

  const getEventName = (eventId: string) => {
    const selectedEvent = events.find((event) => event.id === eventId);

    return selectedEvent ? selectedEvent.name : 'Details';
  };

  const renderBadge = (event: Event) => {
    const pastEvent = compareAsc(event.date.start, new Date()) === -1;
    const participantsNumber = event.participantsNumber || 0;
    const maxParticipants = event.maxParticipants || 0;

    if (!pastEvent && event.signupsBlocked) {
      return (
        <SignupsDisabledWrapper>
          <SignupsDisabledText>Registration soon</SignupsDisabledText>
        </SignupsDisabledWrapper>
      );
    }
    if (pastEvent) {
      return (
        <SignupsDisabledWrapper>
          <SignupsDisabledText>Past event</SignupsDisabledText>
        </SignupsDisabledWrapper>
      );
    }
    if (participantsNumber >= maxParticipants) {
      return (
        <SignupsDisabledWrapper>
          <SignupsDisabledText>No more slots</SignupsDisabledText>
        </SignupsDisabledWrapper>
      );
    }
  };

  const renderedContent = () => {
    if (isLoading && !popupOpen) {
      return (
        <LoaderWrapper>
          <Loader />
        </LoaderWrapper>
      );
    }

    if (error) {
      return <Alert type="error">Oops! Something went wrong: {error}</Alert>;
    }

    if (events.length === 0) {
      return <Text style={{ marginTop: '2rem' }}>No events</Text>;
    }

    return (
      <Events>
        {events.map((event) => {
          const pastEvent = compareAsc(event.date.start, new Date()) === -1;
          const participantsNumber = event.participantsNumber || 0;
          const maxParticipants = event.maxParticipants || 0;

          return (
            <EventCard key={event.id}>
              <ImageWrapper>
                <EventImage src={event.imageUrl} alt={event.name} />
                <DarkOverlay />

                {renderBadge(event)}
              </ImageWrapper>

              <EventTitle>
                <Subtitle>{event.name}</Subtitle>
              </EventTitle>

              <DetailsRow>
                <Label>Date</Label>
                <DetailsText>{renderDateRange(event.date)}</DetailsText>
              </DetailsRow>

              <DetailsRow>
                <Label>Place</Label>
                <DetailsText>{event.location}</DetailsText>
              </DetailsRow>

              <DetailsRow>
                <Label>
                  {event.instructors.length > 1 ? 'Coaches' : 'Coach'}
                </Label>
                <DetailsText $last>{event.instructors.join(', ')}</DetailsText>
              </DetailsRow>

              <ButtonsWrapper $withDetails={Boolean(event?.description)}>
                {event.description && (
                  <TextButton
                    type="button"
                    onClick={() => {
                      setSelectedEventId(event.id!);
                      setDescPopupOpen(true);
                    }}
                  >
                    Details
                  </TextButton>
                )}

                <Button
                  dark
                  long
                  disabled={
                    event.signupsBlocked ||
                    pastEvent ||
                    participantsNumber >= maxParticipants
                  }
                  onClick={() => {
                    if (
                      event.signupsBlocked ||
                      pastEvent ||
                      participantsNumber >= maxParticipants
                    )
                      return;

                    setSelectedEventId(event.id!);
                    dispatch(setSignupPopupOpen(true));
                  }}
                >
                  Sign up
                </Button>
              </ButtonsWrapper>
            </EventCard>
          );
        })}
      </Events>
    );
  };

  return (
    <PageWrapper>
      <Navbar dark />

      <Content>
        <Header>
          <Title>Events</Title>

          <FilteringControls
            selectedMonth={selectedMonth}
            selectedYear={selectedYear}
            setSelectedMonth={(month: string) => handleMonthSelect(month)}
            setSelectedYear={(year: string) => handleYearSelect(year)}
          />
        </Header>

        {successAlertOpen && (
          <Alert type="success">
            You have successfully signed up for the event
          </Alert>
        )}

        {renderedContent()}

        {nextQuery && !error && !isLoading && (
          <LoadMoreEventsButtonWrapper>
            <Button
              dark
              type="button"
              onClick={() => setPage((prev) => prev + 1)}
            >
              See more events
            </Button>
          </LoadMoreEventsButtonWrapper>
        )}
      </Content>

      {popupOpen && (
        <Popup
          title="Sign up for the event"
          handleClose={() => dispatch(setSignupPopupOpen(false))}
          small
        >
          <SignupForm eventId={selectedEventId} />
        </Popup>
      )}

      {descPopupOpen && (
        <Popup
          title={getEventName(selectedEventId)}
          handleClose={() => setDescPopupOpen(false)}
        >
          <Description eventId={selectedEventId} />
        </Popup>
      )}

      <Footer />
    </PageWrapper>
  );
}
